/**
 * Parser del código de producto.
 *
 * Reemplaza el parseo por posición fija (`MID`) de la hoja «PRODUCTO» (Tabla3)
 * del Excel — defecto S2 del análisis: bastaba un espacio de más o un guion
 * distinto para que dejara de aplicarse el recargo, en silencio y sin error.
 *
 * Formato:  GRADO + MPa + (%CONF) + Ø + '-' + CONO + [EXTRAS…]
 *   GN30(90)20-13 AIR    → GN, 30, 90, 20, 13, ['AIR']
 *   SH350(90)10-20       → SH, 350, 90, 10, 20, []
 *   GB35(90)20-13 AIR R7 → GB, 35, 90, 20, 13, ['AIR','R7']
 *   GB50(90)1018         → tolera la falta del guion (aparece 5 veces en NV 17)
 *
 * Ver ANALISIS-PLANILLAS.md §3.7 y §4.1, PROMPT-MAESTRO §5.2.
 */

import type { FamiliaProducto, Grado, ProductoParseado } from './tipos';

/**
 * Grado + MPa + (confianza) + Ø + separador opcional + cono + resto.
 * El separador puede ser '-', '_', espacio o nada.
 */
const RE_HORMIGON =
  /^(GN|GB|SH|G)\s*(\d{2,3})\s*\(\s*(\d{2})\s*\)\s*(\d{1,2})\s*[-_\s]?\s*(\d{1,2})\s*(.*)$/i;

/** Extras reconocidos. El orden importa: los más largos primero. */
const EXTRAS_CONOCIDOS = [
  'FP0,8',
  'FP0.8',
  'FP3',
  'FP5',
  'FP6',
  'AIR',
  'ADI',
  'IMP',
  'R3',
  'R7',
] as const;

/**
 * Productos que NO son hormigón y por tanto no generan recargos.
 * Lista literal tomada de la fórmula de la columna A de Tabla3.
 * En producción vive en la base de datos como configuración, no aquí.
 */
export const PRODUCTOS_SIN_RECARGO: readonly string[] = [
  'ARENA',
  'GRAVILLA',
  'GRAVILA',
  'IBC VACIOS 1000 LTS',
  'CEMENTO ESP',
  'DRENANTE BAJO 3',
  'ESTABILIZADO BAJO 3',
  'TRASLADO',
  'TRASLADO MIXER ESTANDAR',
  'COSTO M3 HORMIGON BOMBEADO',
  'NULA',
  'NULAS',
  'NULO',
  'TRASLADO DE ARENA 20 M3',
  'TRANSPORTE CAMION BOMBA',
  'APERTURA DE PLANTA',
  'TRASLADO DE TORNADO',
  'TRASLADO DE CARGADOR FRONTAL',
  'TRASLADO DE GRUA HORQUILLA',
  'BOMBONAS VACIAS',
  'HORM. PRUEBAS',
  'TRANSPORTE CAMION BOMBA Y 70 M3',
  '',
];

/**
 * Códigos especiales que no siguen la nomenclatura y se resuelven a mano.
 * Aparecen en el histórico de NV 17.
 */
const ESPECIALES: Record<string, Partial<ProductoParseado>> = {
  'H500 R3': { grado: 'G', mpa: 500, extras: ['R3'] },
  'HORMIGON R3 G50 SUMINISTROS ESPECIALES': { grado: 'G', mpa: 50, extras: ['R3'] },
  'HORMIGON G50 R3 SUMINISTRO ESPECIAL': { grado: 'G', mpa: 50, extras: ['R3'] },
  'G-50-R-3': { grado: 'G', mpa: 50, extras: ['R3'] },
  'GN50 R3': { grado: 'GN', mpa: 50, extras: ['R3'] },
};

/**
 * Normaliza un código para comparar y agrupar.
 * Corrige el defecto E10: `GN30(90)20-13 ` con espacio final se contaba
 * como producto distinto (17 filas del histórico).
 */
export function normalizarCodigo(codigo: string): string {
  return codigo
    .trim()
    .toUpperCase()
    .replace(/\s+/g, ' ')
    .replace(/_/g, '-')
    .replace(/\s*-\s*/g, '-')
    .replace(/\s*\(\s*/g, '(')
    .replace(/\s*\)\s*/g, ')');
}

/** Extrae los extras del resto del código, tolerando separadores variados. */
function extraerExtras(resto: string): string[] {
  if (!resto) return [];
  const texto = resto.toUpperCase().replace(/,/g, ',');
  const encontrados: string[] = [];
  for (const extra of EXTRAS_CONOCIDOS) {
    // \b no sirve con la coma de FP0,8: se compara por inclusión con borde manual.
    const re = new RegExp(`(^|[^A-Z0-9])${extra.replace(/[.,]/g, '[.,]')}([^A-Z0-9]|$)`, 'i');
    if (re.test(texto)) {
      const canonico = extra === 'FP0.8' ? 'FP0,8' : extra;
      if (!encontrados.includes(canonico)) encontrados.push(canonico);
    }
  }
  return encontrados;
}

/**
 * Parsea un código de producto.
 * Cuando no lo reconoce devuelve `ok: false` — y la guía entra igual, marcada
 * para clasificación manual. Nunca se descarta ni se pierde plata en silencio.
 */
export function parsearProducto(codigoOriginal: string): ProductoParseado {
  const vacio: ProductoParseado = {
    grado: null,
    mpa: null,
    pctConfianza: null,
    diametroArido: null,
    cono: null,
    extras: [],
    ok: false,
  };

  if (!codigoOriginal) return vacio;
  const codigo = normalizarCodigo(codigoOriginal);

  // 1) No-hormigón conocido → válido, pero sin atributos ni recargos.
  if (PRODUCTOS_SIN_RECARGO.includes(codigo)) {
    return { ...vacio, ok: true };
  }

  // 2) Especiales con nomenclatura propia.
  const especial = ESPECIALES[codigo];
  if (especial) {
    return { ...vacio, ...especial, extras: especial.extras ?? [], ok: true };
  }

  // 3) Nomenclatura estándar.
  const m = RE_HORMIGON.exec(codigo);
  if (!m) return vacio;

  const [, gradoRaw, mpaRaw, confRaw, diamRaw, conoRaw, resto] = m;

  return {
    grado: gradoRaw!.toUpperCase() as Grado,
    mpa: Number(mpaRaw),
    pctConfianza: Number(confRaw),
    diametroArido: Number(diamRaw),
    cono: Number(conoRaw),
    extras: extraerExtras(resto ?? ''),
    ok: true,
  };
}

/** Clasifica el producto en una familia, para filtros e inventario. */
export function familiaDe(codigoOriginal: string, p: ProductoParseado): FamiliaProducto {
  const c = normalizarCodigo(codigoOriginal);
  if (p.grado) return 'HORMIGON';
  if (/^(ARENA|GRAVILLA|GRAVILA|POLVO ROCA|POLVILLO|BOLON|ESTABILIZADO|DRENANTE)/.test(c))
    return 'ARIDO';
  if (/CEMENTO|MORTERO|XTREME RAPIDO/.test(c)) return 'CEMENTO';
  if (/SIKA|ADITIVO|FIBRA/.test(c)) return 'ADITIVO';
  if (/TRASLADO|TRANSPORTE|APERTURA|BOMBEO|ARRIENDO/.test(c)) return 'SERVICIO';
  return 'OTRO';
}

/** ¿Este producto puede generar recargos automáticos? */
export function generaRecargos(codigoOriginal: string, p: ProductoParseado): boolean {
  const c = normalizarCodigo(codigoOriginal);
  if (PRODUCTOS_SIN_RECARGO.includes(c)) return false;
  return p.grado !== null;
}

/** Descripción legible del producto para la UI. */
export function describirProducto(p: ProductoParseado): string {
  if (!p.grado) return 'Sin clasificar';
  const partes: string[] = [];
  const nombreGrado: Record<Grado, string> = {
    GN: 'Hormigón normal',
    GB: 'Hormigón bombeable',
    SH: 'Shotcrete',
    G: 'Hormigón especial',
  };
  partes.push(nombreGrado[p.grado]);
  if (p.mpa !== null) partes.push(`${p.mpa} MPa`);
  if (p.cono !== null) partes.push(`cono ${p.cono}`);
  if (p.pctConfianza !== null) partes.push(`${p.pctConfianza}% conf.`);
  if (p.extras.length) partes.push(p.extras.join(' + '));
  return partes.join(' · ');
}
